import type { DragEndEvent } from '@dnd-kit/core';
import {
  DndContext,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  restrictToHorizontalAxis,
  restrictToParentElement,
  restrictToVerticalAxis,
} from '@dnd-kit/modifiers';
import {
  SortableContext,
  arrayMove,
  horizontalListSortingStrategy,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import type { ReactNode } from 'react';

import { SortableElement } from './sortableElement';
import type { Direction, SortableItem } from './types';
import { verticalSortableListCollisionDetection } from './verticalSortableListCollisionDetection';

export interface SortableListProps<T> {
  className?: string;
  direction?: Direction;
  disabled?: boolean;
  disabledForItem?: (item: SortableItem<T>) => boolean;
  gap?: number;
  hideItem?: (item: SortableItem<T>) => boolean;
  items: SortableItem<T>[];
  onChange: (
    items: SortableItem<T>[],
    oldIndex: number,
    newIndex: number
  ) => void;
  renderItem: (item: SortableItem<T>, index: number) => ReactNode;
  showDragHandle?: boolean;
  variableHeights?: boolean;
}
export function SortableList<T>({
  className,
  direction = 'horizontal',
  disabled,
  disabledForItem,
  gap = 24,
  items,
  onChange,
  renderItem,
  hideItem,
  showDragHandle = true,
  variableHeights = false,
}: SortableListProps<T>) {
  const isHorizontal = direction === 'horizontal';
  const onDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (over && active.id !== over.id) {
      const oldIndex = items.findIndex(
        (item) => `${item.id}` === `${active.id}`
      );
      const newIndex = items.findIndex((item) => `${item.id}` === `${over.id}`);
      const newItems = arrayMove(items, oldIndex, newIndex);
      onChange(newItems, oldIndex, newIndex);
    }
  };
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 10 },
    })
  );

  return (
    <DndContext
      modifiers={[
        isHorizontal ? restrictToHorizontalAxis : restrictToVerticalAxis,
        restrictToParentElement,
      ]}
      onDragEnd={onDragEnd}
      accessibility={{
        screenReaderInstructions: { draggable: '' },
      }}
      sensors={sensors}
      collisionDetection={
        variableHeights ? verticalSortableListCollisionDetection : undefined
      }
    >
      <SortableContext
        strategy={
          isHorizontal
            ? horizontalListSortingStrategy
            : verticalListSortingStrategy
        }
        items={items.map((item) => item.id)}
      >
        <ul
          className={className}
          style={{
            display: 'flex',
            flexDirection: isHorizontal ? 'row' : 'column',
            gap: `${gap}px`,
          }}
        >
          {items
            .filter((item) => !(hideItem && hideItem(item)))
            .map((item, index) => (
              <SortableElement
                disabled={
                  disabled ||
                  (disabledForItem ? disabledForItem(item) : undefined)
                }
                key={item.id}
                id={item.id}
                showDragHandle={showDragHandle}
                direction={direction}
                variableHeights={variableHeights}
              >
                {renderItem(item, index)}
              </SortableElement>
            ))}
        </ul>
      </SortableContext>
    </DndContext>
  );
}
